import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-provider";
import { api } from "@/lib/api";
import { AppShell } from "@/components/app-shell";

interface CategoryInfo {
  id: string;
  name: string;
  description: string;
  count: number;
  completed?: number;
  trained?: number;
  skippedOnly?: number;
}

const CATEGORY_ICONS: Record<string, string> = {
  deposit: "💰",
  withdrawal: "🏧",
  bonus: "🎁",
  account: "👤",
  game: "🎮",
  general: "💬",
};

export default function Home() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [unreadReviews, setUnreadReviews] = useState(0);

  useEffect(() => {
    api<{ categories: CategoryInfo[] }>("/api/categories")
      .then((data) => setCategories(data.categories ?? []))
      .catch((err) => console.error("Failed to load categories", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchNotifications = () => {
      api<{ unreadCount: number }>("/api/notifications")
        .then((data) => {
          if (!cancelled) setUnreadReviews(data.unreadCount ?? 0);
        })
        .catch(() => {});
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const totalActive = categories.reduce((sum, c) => sum + c.count, 0);
  const totalTrained = categories.reduce((sum, c) => sum + (c.trained ?? c.completed ?? 0), 0);
  const totalSkipped = categories.reduce((sum, c) => sum + (c.skippedOnly ?? 0), 0);
  const overallPct = totalActive > 0 ? Math.round((totalTrained / totalActive) * 100) : 0;

  return (
    <AppShell
      breadcrumbs={[{ label: "Home" }]}
      actions={
        unreadReviews > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/reviews")}
            className="relative"
          >
            <span>Reviews</span>
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadReviews}
            </span>
          </Button>
        ) : null
      }
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        {/* Greeting strip */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome back{profile?.name ? `, ${profile.name}` : ""}.
            </h1>
            <p className="text-sm text-muted-foreground">
              Practice with real customer conversations. Pick a category to start.
            </p>
          </div>
          {totalActive > 0 && (
            <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-2 shadow-xs">
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Trained
                </div>
                <div className="text-lg font-semibold tabular-nums">
                  {totalTrained}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    / {totalActive}
                  </span>
                </div>
                {totalSkipped > 0 && (
                  <div className="text-[10px] text-orange-600 tabular-nums">
                    +{totalSkipped} skip-only
                  </div>
                )}
              </div>
              <div className="h-10 w-1 rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    height: `${overallPct}%`,
                    width: "100%",
                    marginTop: `${100 - overallPct}%`,
                  }}
                />
              </div>
              <span className="text-2xl font-semibold tabular-nums text-primary">
                {overallPct}%
              </span>
            </div>
          )}
        </div>

        {/* Category grid */}
        <section>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Categories
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat) => {
              const completed = cat.completed ?? 0;
              const trained = cat.trained ?? completed;
              const skipped = cat.skippedOnly ?? Math.max(0, completed - trained);
              const trainedPct = cat.count > 0 ? Math.round((trained / cat.count) * 100) : 0;
              const isDone = trained >= cat.count && cat.count > 0;
              return (
                <Card
                  key={cat.id}
                  className="group cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md"
                  onClick={() => navigate(`/train?category=${cat.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <span className="text-3xl leading-none">{CATEGORY_ICONS[cat.id] ?? "📋"}</span>
                      {isDone && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                          ✓ All trained
                        </span>
                      )}
                    </div>
                    <CardTitle className="mt-2">{cat.name}</CardTitle>
                    <CardDescription className="line-clamp-2">{cat.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium tabular-nums">
                        {trained} <span className="text-muted-foreground">/ {cat.count}</span>
                      </span>
                      <span className="text-xs text-muted-foreground">{trainedPct}%</span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full transition-all ${isDone ? "bg-green-500" : "bg-primary"}`}
                        style={{ width: `${trainedPct}%` }}
                      />
                    </div>
                    {skipped > 0 && (
                      <p className="mt-1.5 text-[11px] text-orange-600 tabular-nums">
                        +{skipped} clicked through without typing
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Retrain skipped — only when there's something to retrain */}
        {totalSkipped > 0 && (
          <section>
            <Card
              className="cursor-pointer border-orange-200 bg-orange-50/50 transition-all hover:-translate-y-0.5 hover:shadow-md"
              onClick={() => navigate("/train/retrain-skipped")}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">⏪</span> Retrain {totalSkipped} skipped
                  conversation{totalSkipped === 1 ? "" : "s"}
                </CardTitle>
                <CardDescription>
                  You completed these without typing a reply. Walk through them in a focused
                  queue — type a real response so they count as trained.
                </CardDescription>
              </CardHeader>
            </Card>
          </section>
        )}

        {/* Side modules */}
        <section>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
            More
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card
              className="cursor-pointer border-purple-200 bg-purple-50/40 transition-all hover:-translate-y-0.5 hover:shadow-md"
              onClick={() => navigate("/special")}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">⭐</span> Special Scenarios
                </CardTitle>
                <CardDescription>
                  Real customer questions from VIP clients. Your replies will be reviewed.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card
              className="cursor-pointer border-yellow-200 bg-yellow-50/40 transition-all hover:-translate-y-0.5 hover:shadow-md"
              onClick={() => navigate("/reviews/best")}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">🏆</span> Best Responses
                </CardTitle>
                <CardDescription>
                  Top trainee responses picked by reviewers. Leaderboard and recent picks.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
